import React, { useRef, useState } from 'react';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

// components
import ProjectsHeader from '../shared/ProjectsHeader';

// helpers
import {
  createPromptToDesign,
  generatePromptToDesign,
  getPromptToDesign,
} from '../../api/helpers';
import { SUGGESTIONS_ARRAY } from '../../helpers/common.data';

// types
import { IProjectsTabContent } from '../../types/studio.types';

interface IP2DBrandSetForm {
  purpose: string;
}

const ProjectsPromptToBrand = ({
  searchParams,
  setSearchParams,
  isDarkMode,
  toggleDarkMode,
  onDisableTab,
  onEnableTab,
  isTabDisabled,
}: IProjectsTabContent) => {
  const [isInputDisabled, setIsInputDisabled] = useState<boolean>(false);
  const [p2dError, setP2dError] = useState('');
  const [isPromptToDesignLoading, setIsPromptToDesignLoading] =
    useState<boolean>(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const inputContainerRef = useRef(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IP2DBrandSetForm>({
    defaultValues: {
      purpose: '',
    },
  });

  const navigate = useNavigate();

  const onGeneratePromptToDesign = async (
    id: number,
    designsAmount?: number,
  ) => {
    let response = null;

    response = await getPromptToDesign(id);

    if (
      response.formatExtenderStatus == 'failed' ||
      response.status === 'failed'
    ) {
      setP2dError(
        response.errorMessage ||
          'Encountered an error during Prompt-to-Design, please try again later.',
      );
      setIsInputDisabled(false);
      setIsPromptToDesignLoading(false);
      onEnableTab?.();
    } else if (
      response.formatExtenderStatus === 'finished' &&
      response.status === 'finished'
    ) {
      navigate({
        pathname: `/projects`,
        search: `?folder=${response.folder}&type=brand`,
      });
      onEnableTab?.();
    } else {
      await new Promise((resolve) => setTimeout(resolve, 5000));
      await onGeneratePromptToDesign(id, designsAmount);
    }
  };

  const onInputFocus = () => {
    setIsInputFocused(true);
  };

  const onInputBlur = () => {
    setIsInputFocused(false);
  };

  const onSubmit = async (formValues: IP2DBrandSetForm) => {
    setIsInputDisabled(true);
    setIsPromptToDesignLoading(true);
    onDisableTab?.();

    const data = {
      purpose: formValues.purpose,
      extendFormats: true,
    };

    const createP2DResponse = await createPromptToDesign(data);

    try {
      await generatePromptToDesign(createP2DResponse?.id, data);
      onGeneratePromptToDesign(createP2DResponse?.id);
    } catch (error) {
      onEnableTab?.();
    }
  };

  const onP2dRandomize = () => {
    if (inputContainerRef.current) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (inputContainerRef.current as any).childNodes[0].focus();
    }
    setIsInputFocused(true);
    setValue(
      'purpose',
      SUGGESTIONS_ARRAY[Math.floor(Math.random() * 10)].message,
      {
        shouldValidate: true,
      },
    );
  };

  return (
    <div className="projects-prompt-to-brand">
      <ProjectsHeader
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        isDarkMode={isDarkMode}
        toggleDarkMode={toggleDarkMode}
        title="Brand Set"
        isNewDesignBtnVisible={false}
        isTabDisabled={isTabDisabled}
      />
      <div className="projects-p2d-container">
        <div className="projects-p2d-container--brand-set-container dark:!bg-[#373737]">
          <p className="projects-p2d-container--title dark:!text-[#eeeeee]">
            Prompt-to-Brand Set
          </p>
          <form
            className="projects-p2d-container--brand-set-container--input-container"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div
              className={cn(
                'p2d-container--content--input-container--randomize-btn h-[40px] dark:!bg-[#2d2d2d]',
                {
                  light: !isDarkMode && typeof isDarkMode === 'boolean',
                  disabled: isInputDisabled,
                },
              )}
              onClick={onP2dRandomize}
              onFocus={onInputFocus}
              onBlur={onInputBlur}
            >
              <img
                src={`../../../../static/img/icons/randomize-p2d${
                  !isDarkMode && typeof isDarkMode === 'boolean'
                    ? '-light-mode'
                    : ''
                }.svg`}
                alt="randomize"
              />
            </div>
            <div className="input-inner-container" ref={inputContainerRef}>
              <input
                {...register('purpose', {
                  required: true,
                  minLength: {
                    value: 6,
                    message: 'Purpose must be at least 6 characters long',
                  },
                  maxLength: {
                    value: 255,
                    message: 'Purpose must be at most 255 characters long',
                  },
                })}
                type="text"
                className={cn(
                  'p2d-container--content--input-container--field h-[40px] dark:!text-[#eeeeee]',
                  {
                    'focused-light': isInputFocused && !isDarkMode,
                    focused: isInputFocused,
                    disabled: isInputDisabled,
                    light: !isDarkMode && typeof isDarkMode === 'boolean',
                  },
                )}
                placeholder="What do you want to design?"
              />
            </div>

            <button
              type="submit"
              className={cn(
                'projects-p2d-container--brand-set-container--input-container--generate-btn',
                {
                  disabled: !watch('purpose') || isPromptToDesignLoading,
                },
              )}
            >
              generate
            </button>
          </form>
          {(p2dError || errors.purpose) && (
            <p className="p2d-container--content--error">
              {p2dError || errors.purpose?.message}
            </p>
          )}
          {isPromptToDesignLoading && (
            <div>
              <div
                className={cn(
                  'p2d-container--content--generation-container--item--image-container',
                  {
                    skeleton: isPromptToDesignLoading,
                    'skeleton-dark': !isDarkMode,
                  },
                )}
              />
              <button
                type="button"
                aria-label="Generate Design"
                className={cn(
                  'p2d-container--content--generation-container--item--btn-container--btn mt-1.5',
                  {
                    skeleton: isPromptToDesignLoading,
                    'skeleton-dark': !isDarkMode,
                  },
                )}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectsPromptToBrand;
